import React, { useState, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { withUrlValidation } from '../components/withUrlValidation';
import { soundManager } from '../utils/soundUtils';
import { incrementEndingReachedUsers } from '../utils/funnelDataUtils';
import endingBg from '../assets/images/ending-bg.png';
import endingPop from '../assets/images/ending-pop.png';
import endingBtn from '../assets/images/ending-btn.png';
import endingPop1 from '../assets/images/ending-pop1.png';
import endingPop2 from '../assets/images/ending-pop2.png';
import endingPop3 from '../assets/images/ending-pop3.png';
import endingPopr from '../assets/images/ending-popr.png';
import endingPopOk from '../assets/images/ending-pop-ok.png';
import contractNo from '../assets/images/03contract-no.png';
import contractYes from '../assets/images/03contract-yes.png';
import gradation from '../assets/images/gradation.png';
import SentMessages from '../components/SentMessages';
import Bookmark from '../components/Bookmark';

const Container = styled.div<{ showBlackBg: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100dvw;
  height: 100dvh;
  background: ${props => props.showBlackBg ? 'black' : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'MapoDPP', sans-serif;
  color: white;
  overflow: hidden;
`;


const backgroundSlideAnimation = keyframes`
  0% {
    opacity: 0;
    transform: translateY(0);
  }
  100% {
    opacity: 1;
    transform: translateY(-16.67%);
  }
`;

const BackgroundImage = styled.div<{ isVisible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 120%;
  background-image: url(${endingBg});
  background-size: cover;
  background-position: center top;
  background-repeat: no-repeat;
  opacity: ${props => props.isVisible ? 1 : 0};
  visibility: ${props => props.isVisible ? 'visible' : 'hidden'};
  will-change: opacity, transform;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  transform-style: preserve-3d;
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
  ${props => props.isVisible && css`
    animation: ${backgroundSlideAnimation} 3s ease-out forwards;
  `}
  z-index: 1;
`;

const popupFadeInAnimation = keyframes`
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translate(-50%, -50%) translateY(0);
  }
`;

const popupBackgroundFadeInAnimation = keyframes`
  0% {
    opacity: 0;
    transform: translateX(-50%) translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
`;

const PopupImage = styled.img<{ isVisible: boolean }>`
  position: absolute;
  top: 70%;
  left: 50%;
  transform: translate(-50%, -50%);
  max-width: 90%;
  height: auto;
  opacity: ${props => props.isVisible ? 1 : 0};
  visibility: ${props => props.isVisible ? 'visible' : 'hidden'};
  will-change: opacity, transform;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  transform-style: preserve-3d;
  ${props => props.isVisible && css`
    animation: ${popupFadeInAnimation} 1s ease-out;
  `}
  z-index: 20;
  cursor: pointer;
`;

const PopupBackground = styled.div<{ isVisible: boolean }>`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  height: auto;
  aspect-ratio: 1;
  background-image: url(${gradation});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center bottom;
  opacity: ${props => props.isVisible ? 1 : 0};
  visibility: ${props => props.isVisible ? 'visible' : 'hidden'};
  will-change: opacity, transform;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  transform-style: preserve-3d;
  ${props => props.isVisible && css`
    animation: ${popupBackgroundFadeInAnimation} 1s ease-out;
  `}
  z-index: 19;
`;

const PopupOverlayImage = styled.img<{ isVisible: boolean }>`
  max-width: 90%;
  height: auto;
`;

const buttonFadeInAnimation = keyframes`
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) translateY(30px);
  }
  100% {
    opacity: 1;
    transform: translate(-50%, -50%) translateY(0);
  }
`;

const ButtonImage = styled.img<{ isVisible: boolean }>`
  position: absolute;
  top: calc(70% + 35dvw);
  left: 50%;
  transform: translate(-50%, -50%);
  max-width: 80%;
  height: auto;
  opacity: ${props => props.isVisible ? 1 : 0};
  visibility: ${props => props.isVisible ? 'visible' : 'hidden'};
  will-change: opacity, transform;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  transform-style: preserve-3d;
  ${props => props.isVisible && css`
    animation: ${buttonFadeInAnimation} 1s ease-out;
  `}
  z-index: 30;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: translate(-50%, -50%) scale(1.05);
  }

  &:active {
    transform: translate(-50%, -50%) scale(0.95);
  }
`;

const fadeToBlackAnimation = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const BlackOverlay = styled.div<{ isVisible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: black;
  opacity: ${props => props.isVisible ? 1 : 0};
  animation: ${fadeToBlackAnimation} 2s ease-in-out;
  z-index: 100;
  pointer-events: ${props => props.isVisible ? 'auto' : 'none'};
`;

const TVShutdownOverlay = styled.div<{ isActive: boolean; progress: number }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 1000;
  pointer-events: none;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: ${props => props.progress * 50}%;
    background: black;
    transition: height 1s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: ${props => props.progress * 50}%;
    background: black;
    transition: height 1s ease;
  }
`;

const fadeInAnimation = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const fadeOutAnimation = keyframes`
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`;

const PopupOverlay = styled.div<{ isVisible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
  opacity: ${props => props.isVisible ? 1 : 0};
  transition: opacity 1s ease-in-out;
  pointer-events: ${props => props.isVisible ? 'auto' : 'none'};
`;

const FirstPopupOverlay = styled.div<{ isVisible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
  opacity: 1;
  pointer-events: ${props => props.isVisible ? 'auto' : 'none'};
`;

const ButtonContainer = styled.div`
  position: absolute;
  bottom: 25%;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 10px;
  width: 80%;
  justify-content: center;
  z-index: 60;
`;

const Button = styled.img`
  width : 50%;
  height: auto;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ConfirmButton = styled.img`
  max-width: 80dvw;
  height: auto;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;



const EndingPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0); // 초기값을 0으로 설정
  const [showBlackOverlay, setShowBlackOverlay] = useState(false);
  const [showTVShutdown, setShowTVShutdown] = useState(false);
  const [tvShutdownProgress, setTvShutdownProgress] = useState(0);
  const [coinCount, setCoinCount] = useState(0);
  const [showSentMessages, setShowSentMessages] = useState(false);
  const [showBookmarkPopup, setShowBookmarkPopup] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // 이미지 프리로딩
    const preloadImages = () => {
      const imageUrls = [endingBg, endingPop, endingBtn, endingPop1, endingPop2, endingPop3, endingPopr, endingPopOk, gradation];
      
      const imagePromises = imageUrls.map(url => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = resolve;
          img.onerror = reject;
          img.src = url;
        });
      });
      
      Promise.all(imagePromises)
        .then(() => {
          setImagesLoaded(true);
        })
        .catch(error => {
          console.error('이미지 로딩 오류:', error);
          setImagesLoaded(true); // 오류가 있어도 계속 진행
        });
    };
    
    preloadImages();
    
    // 엔딩 도달 유저 수 추적 (체험 완료율 계산용)
    incrementEndingReachedUsers();
  }, []);

  useEffect(() => {
    // 사용자의 코인 수 가져오기
    const currentUser = localStorage.getItem('currentUser');
    if (currentUser) {
      try {
        const userData = JSON.parse(currentUser);
        setCoinCount(userData.coinCount || 0);
      } catch (error) {
        console.error('사용자 데이터 파싱 오류:', error);
      }
    }
  }, []);

  useEffect(() => {
    // 이미지 로딩 완료 후에만 애니메이션 시작
    if (!imagesLoaded) return;
    
    // 2초 후 첫 번째 팝업 시작
    const timer1 = setTimeout(() => {
      setCurrentStep(1);
    }, 2000);

    // 첫 번째 팝업 페이드아웃 후 두 번째 팝업 시작
    const timer2 = setTimeout(() => {
      setCurrentStep(2); // 페이드아웃 단계
    }, 4500);

    // 페이드아웃 완료 후 두 번째 팝업 시작
    const timer3 = setTimeout(() => {
      setCurrentStep(3);
    }, 5500);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [imagesLoaded]);

  const handleYesClick = () => {
    // YES 버튼 클릭 시 배경 이미지 페이드인
    setCurrentStep(12);

    // 배경 애니메이션 완료 후 PopupBackground 나타남 (3초 후)
    const timer1 = setTimeout(() => {
      setCurrentStep(13);
    }, 3000);

    // PopupBackground 나타난 후 endingPop과 버튼 동시에 나타남 (1초 후)
    const timer2 = setTimeout(() => {
      setCurrentStep(14);
    }, 4000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  };

  const handleNoClick = () => {
    if (coinCount < 200) {
      // 코인이 200개 미만인 경우 ending-pop3.png 표시
      setCurrentStep(4);
    } else {
      // 코인이 200개 이상인 경우 ending-popr.png 표시
      setCurrentStep(6);
    }
  };

  const handleConfirmClick = () => {
    // ending-pop3.png의 확인 버튼 클릭 시 북마크 팝업 표시
    setShowBookmarkPopup(true);
  };

  const handleRichNoClick = () => {
    // ending-popr.png에서 NO 클릭 시 배경 이미지 페이드인
    setCurrentStep(12);

    // 배경 애니메이션 완료 후 PopupBackground 나타남 (3초 후)
    const timer1 = setTimeout(() => {
      setCurrentStep(13);
    }, 3000);

    // PopupBackground 나타난 후 endingPop과 버튼 동시에 나타남 (1초 후)
    const timer2 = setTimeout(() => {
      setCurrentStep(14);
    }, 4000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  };

  const handleRichYesClick = () => {
    // ending-popr.png에서 YES 클릭 시 message-send 페이지로 이동
    navigate('/message-send');
  };

  const handleButtonClick = () => {
    soundManager.playButtonClick();
    // 팝업과 버튼 페이드아웃
    setCurrentStep(15); // 페이드아웃 상태
    
    // 0.5초 후 TV 셧다운 연출 시작
    setTimeout(() => {
      setShowTVShutdown(true);
      
      // 0.5초 후 위아래에서 검은색이 채워지기 시작
      setTimeout(() => {
        setTvShutdownProgress(1);
        
        // TV 셧다운 애니메이션 완료 후 광고페이지로 이동
        setTimeout(() => {
          navigate('/ad');
        }, 1000); // 1초 후 광고페이지로 이동
      }, 500);
    }, 500);
  };

  const handleGoToEndingClick = () => {
    // 배경 이미지 페이드인
    setCurrentStep(12);

    // 배경 애니메이션 완료 후 PopupBackground 나타남 (3초 후)
    const timer1 = setTimeout(() => {
      setCurrentStep(13);
    }, 3000);

    // PopupBackground 나타난 후 endingPop과 버튼 동시에 나타남 (1초 후)
    const timer2 = setTimeout(() => {
      setCurrentStep(14);
    }, 4000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  };

  return (
    <Container showBlackBg={currentStep >= 3 && currentStep < 12}>
      {/* 첫 번째 팝업 (ending-pop1.png) */}
      {(currentStep === 1 || currentStep === 2) && (
        <FirstPopupOverlay isVisible={currentStep === 1 || currentStep === 2}>
          <PopupOverlayImage 
            src={endingPop1} 
            alt="Ending Popup 1" 
            isVisible={true}
            style={{ 
              opacity: currentStep === 2 ? 0 : 1,
              transition: currentStep === 2 ? 'opacity 1s ease-in-out' : 'none',
              animation: currentStep === 1 ? 'none' : 'none'
            }}
          />
        </FirstPopupOverlay>
      )}

      {/* 두 번째 팝업 (ending-pop2.png) */}
      <PopupOverlay isVisible={currentStep === 3}>
        <PopupOverlayImage 
          src={endingPop2} 
          alt="Ending Popup 2" 
          isVisible={currentStep === 3}
        />
        <ButtonContainer>
          <Button 
            src={contractNo} 
            alt="No" 
            onClick={handleNoClick}
          />
          <Button 
            src={contractYes} 
            alt="Yes" 
            onClick={handleYesClick}
          />
        </ButtonContainer>
      </PopupOverlay>

      {/* 세 번째 팝업 (ending-pop3.png) - 코인 부족 */}
      <PopupOverlay isVisible={currentStep === 4}>
        <PopupOverlayImage 
          src={endingPop3} 
          alt="Ending Popup 3" 
          isVisible={currentStep === 4}
        />
        <ButtonContainer>
          <ConfirmButton 
            src={endingPopOk} 
            alt="Confirm" 
            onClick={handleConfirmClick}
          />
        </ButtonContainer>
      </PopupOverlay>

      {/* 부자 팝업 (ending-popr.png) - 코인 200개 이상 */}
      <PopupOverlay isVisible={currentStep === 6}>
        <PopupOverlayImage 
          src={endingPopr} 
          alt="Ending Popup Rich" 
          isVisible={currentStep === 6}
        />
        <ButtonContainer>
          <Button 
            src={contractNo} 
            alt="No" 
            onClick={handleRichNoClick}
          />
          <Button 
            src={contractYes} 
            alt="Yes" 
            onClick={handleRichYesClick}
          />
        </ButtonContainer>
      </PopupOverlay>

      {/* 배경 이미지 */}
      <BackgroundImage isVisible={currentStep >= 12} />
      
      {/* 팝업 배경 (gradation) */}
      <PopupBackground 
        isVisible={currentStep >= 13} 
      />
      
      {/* 팝업 이미지 */}
      <PopupImage 
        src={endingPop} 
        alt="Ending Popup" 
        isVisible={currentStep >= 14 && currentStep < 15} 
      />
      
      {/* 버튼 이미지 */}
      <ButtonImage 
        src={endingBtn} 
        alt="Ending Button" 
        isVisible={currentStep >= 14 && currentStep < 15}
        onClick={handleButtonClick}
      />
      
      {/* 검은색 오버레이 */}
      <BlackOverlay isVisible={showBlackOverlay} />
      
      {/* TV 셧다운 오버레이 */}
      <TVShutdownOverlay 
        isActive={showTVShutdown} 
        progress={tvShutdownProgress}
      />
      
      
      {/* 북마크 컴포넌트 */}
      <Bookmark 
        visible={0} 
        showPopup={showBookmarkPopup}
        onPopupChange={setShowBookmarkPopup}
        zIndex={20000}
        disableProgress={false}
        showGoToEndingButton={true}
        onGoToEndingClick={handleGoToEndingClick}
      />
      
      {/* SentMessages 컴포넌트 */}
      {showSentMessages && (
        <SentMessages onClose={() => {
          setShowSentMessages(false);
        }} />
      )}
    </Container>
  );
};

export default withUrlValidation(EndingPage, {
  requireValidation: false,
  showCountdown: false
});
